import { useCallback, useEffect, useRef, useState } from "react";
import { fetchWalletDetail, fetchWallets, type ApiErrorInfo } from "@/lib/api";
import { formatAgo } from "@/lib/format-ago";
import { parseUsdValue } from "@/features/valuation/valuation";
import type { Wallet } from "@/types";
import { walletDisplayName } from "@/lib/wallet-display-name";

interface UseWalletsResult {
  wallets: Wallet[];
  loading: boolean;
  error: ApiErrorInfo | null;
  source: "api";
  refresh: () => void;
}

/**
 * How many wallet-detail reads run at once when the list is valued (ALL-5638).
 * A demo portfolio is ~21 wallets; four at a time keeps the columns filling in
 * visibly without turning one page load into 21 simultaneous calls.
 */
const VALUATION_CONCURRENCY = 4;

function nonBlankString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/** Gateway `balanceStatus` (upper snake) to the UI's lowercase union. */
function mapBalanceStatus(raw: Record<string, unknown>): Wallet["balanceStatus"] {
  const value = nonBlankString(raw.balanceStatus)?.toUpperCase();
  switch (value) {
    case "OK": return "ok";
    case "UNAVAILABLE": return "unavailable";
    case "UNSUPPORTED": return "unsupported";
    case "NOT_READ": return "not_read";
    // An absent or unrecognised status is not a claim that the read succeeded.
    default: return undefined;
  }
}

export function mapApiWallet(raw: Record<string, unknown>): Wallet {
  const id = String(raw.walletId ?? raw.id ?? "");
  // ALL-5795: a machine-made alias (trio_<hex>, Name_<timestamp>, the id) gets a readable name.
  const display = walletDisplayName({
    alias: nonBlankString(raw.displayName) ?? nonBlankString(raw.accountAlias) ?? nonBlankString(raw.name) ?? nonBlankString(raw.label) ?? null,
    walletId: id,
    network: nonBlankString(raw.network),
    chain: nonBlankString(raw.chain),
    address: nonBlankString(raw.address),
  });
  const alias = display.name;
  const chain = String(raw.chain ?? raw.network ?? "Ethereum Sepolia");
  // ALL-5638: the gateway names the wallet's provider route (ALL-5553). A row
  // with none renders as the dash; the previous default of "Mesh (VaultCore)"
  // labelled Fireblocks and sandbox wallets as a signing route they are not on.
  const provider = nonBlankString(raw.provider) ?? nonBlankString(raw.custodian);
  const rawType = String(raw.type ?? raw.walletType ?? "hot").toLowerCase();
  const type = (rawType === "cold" || rawType === "warm" ? rawType : "hot") as Wallet["type"];
  const rawStatus = String(raw.status ?? "active").toLowerCase();
  const status: Wallet["status"] =
    rawStatus === "frozen" ? "frozen"
    : rawStatus === "pending" ? "pending"
    : rawStatus.includes("sandbox") || rawStatus.includes("test") ? "sandbox"
    : "active";
  // A wallet receives USD only from its gateway detail valuation.
  const balanceUsd = null;
  // ALL-5638: freshness comes from the balance read the gateway actually did.
  const lastSyncAgo = formatAgo(nonBlankString(raw.balancesAsOf));
  // ALL-4884: GatewayWalletResponse.signingTopology, e.g. "TRIO_2OF3" once a
  // wallet is bound to the Silence Trio phone co-signer.
  const signingTopology = typeof raw.signingTopology === "string" ? raw.signingTopology : null;

  return {
    id, name: alias, chain, provider, type, balanceUsd, lastSyncAgo, status, signingTopology,
    address: nonBlankString(raw.address) ?? null,
    storedAlias: display.derived && display.alias ? display.alias : null,
    balanceStatus: mapBalanceStatus(raw),
    balanceSupportReference: nonBlankString(raw.balanceSupportReference),
  };
}

function valuationRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

/** Prefer valued USD, then the gateway-sourced reference total; absent values stay unknown. */
export function applyWalletValuation(wallet: Wallet, detail: Record<string, unknown>): Wallet {
  const valuation = valuationRecord(detail.valuation);
  const valued = parseUsdValue(valuation?.valuedTotal as string | number | null | undefined);
  if (valued !== null) return { ...wallet, balanceUsd: valued };

  const reference = parseUsdValue(valuation?.referenceTotal as string | number | null | undefined);
  return { ...wallet, balanceUsd: reference };
}

/** Runs `worker` over `items` with at most `limit` in flight; never rejects. */
async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>,
): Promise<void> {
  let next = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const item = items[next];
      next += 1;
      await worker(item);
    }
  });
  await Promise.all(runners);
}

export function useWallets(options: { enabled?: boolean } = {}): UseWalletsResult {
  // `enabled: false` defers the whole load (list plus per-wallet valuation
  // reads) until the caller needs it, e.g. the AI search overlay only loads
  // wallets once it is opened.
  const enabled = options.enabled !== false;
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<ApiErrorInfo | null>(null);
  const [source, setSource] = useState<"api">("api");
  // Identifies the current list load. A detail answer that arrives after a
  // refresh, or after unmount, belongs to a stale load and is dropped rather
  // than written over the newer rows.
  const loadIdRef = useRef(0);

  const valueWallets = useCallback(async (rows: Wallet[], loadId: number) => {
    // Only rows whose balances the list actually read can be valued. A
    // NOT_READ row (list row cap) or a failed/unsupported read is unknown, and
    // asking for its detail would not change that.
    const targets = rows.filter((wallet) => wallet.balanceStatus === "ok").map((wallet) => wallet.id);

    await runWithConcurrency(targets, VALUATION_CONCURRENCY, async (id) => {
      let detail: Record<string, unknown> | null = null;
      try {
        const result = await fetchWalletDetail(id);
        // `source: "list"` is the fallback body, which never carries a
        // valuation: using it would present a list row as a priced wallet.
        if (result.source === "detail" && result.wallet) detail = result.wallet;
      } catch {
        // A detail failure leaves this row's balance unknown. It never fails
        // or blocks the list.
        return;
      }
      if (detail === null || loadIdRef.current !== loadId) return;
      setWallets((current) =>
        current.map((wallet) => (wallet.id === id ? applyWalletValuation(wallet, detail as Record<string, unknown>) : wallet)),
      );
    });
  }, []);

  const load = useCallback(async () => {
    const loadId = loadIdRef.current + 1;
    loadIdRef.current = loadId;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchWallets();
      if (loadIdRef.current !== loadId) return;
      if (result.error) {
        setWallets([]);
        setSource("api");
        setError(result.error);
      } else {
        // An empty list is a real answer: this organization has no wallets yet.
        // Substituting MOCK_WALLETS here made an empty sandbox render "8 wallets,
        // $48.5M AUM" on /wallets and /home, which is indistinguishable from real
        // customer data (pipeline G6: fabricated values are mock data).
        const mapped = result.wallets.map(mapApiWallet);
        setWallets(mapped);
        setSource("api");
        // Fire-and-forget: the list is already rendered; each row's USD arrives
        // on its own and a slow or failed read never delays it.
        void valueWallets(mapped, loadId);
      }
    } catch {
      if (loadIdRef.current !== loadId) return;
      setWallets([]);
      setSource("api");
      setError({ reasonCode: "NETWORK_ERROR" });
    } finally {
      if (loadIdRef.current === loadId) setLoading(false);
    }
  }, [valueWallets]);

  useEffect(() => { if (enabled) void load(); }, [enabled, load]);

  // Abandon in-flight valuation writes on unmount.
  useEffect(() => () => { loadIdRef.current += 1; }, []);

  return { wallets, loading, error, source, refresh: load };
}
